// Ported from dev-scrips-pm-react src/documents/services/use-radiology-codes.ts (PROD-1119 documents module).
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { ProcedureCodeRow } from './use-procedure-codes';

/**
 * GET /api/Master/RadiologyCodesList?searchQuery={q}
 *
 * Master-data typeahead for radiology codes. Used by the Radiology
 * document sub-form (PROD-797 PR 4) to populate `data.title` +
 * `data.code` on the patient document body.
 *
 * Flutter parity: `radiology_document_widget.dart` →
 * `FetchRadiologyCodeUseCase` → `common_data_source_impl.dart`
 * `radiologyCodeList()` (`/api/Master/RadiologyCodesList`).
 *
 * Response shape (LIVE-PROBED 2026-06-10) — NOT identical to Procedure:
 *   `{ "Id": "<guid>", "code": { "code": "78451",
 *      "display": "HT MUSCLE IMAGE SPECT SING", "system": null } }`
 * i.e. the row id is `Id` (Procedure ships `specialitySkillId`) and the
 * display string is `code.display` (Procedure ships `code.displayName`).
 * `ProcedureCodeRow` is a tolerant union covering both; `pickedCodeToData`
 * + `CodePicker` read `displayName ?? display` and `Id ?? specialitySkillId`
 * so radiology rows render the display, not code-only.
 *
 * Empty queries short-circuit. 1-hour staleTime.
 *
 * @see {@link ../../../docs/CONVERSION_NOTES_PROD-797.md} (PR 4 wire-shape gotcha table)
 */

export type RadiologyCodeRow = ProcedureCodeRow;

export function useRadiologyCodes(searchQuery: string) {
  const trimmed = searchQuery.trim();
  return useQuery<RadiologyCodeRow[]>({
    queryKey: ['radiology-codes', trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed });
      return get<RadiologyCodeRow[]>(
        `/api/Master/RadiologyCodesList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: trimmed.length > 0,
    staleTime: 60 * 60 * 1000,
  });
}
